import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Captcha } from '../components/Captcha';
import { PasswordStrengthMeter, scorePassword } from '../components/PasswordStrengthMeter';
import { CheckCircle, XCircle, CircleNotch } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RegisterPage() {
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    date_of_birth: '',
    captcha_id: '',
    captcha_answer: '',
    agreeTerms: false,
    confirmAge: false
  });
  const [loading, setLoading] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [usernameStatus, setUsernameStatus] = useState({ state: 'idle', message: '', suggestions: [] });
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const value = formData.username.trim();
    if (!value) {
      setUsernameStatus({ state: 'idle', message: '', suggestions: [] });
      return;
    }
    setUsernameStatus({ state: 'checking', message: 'Checking availability...', suggestions: [] });
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/auth/check-username`, { params: { username: value } });
        setUsernameStatus({
          state: data.available ? 'available' : 'taken',
          message: data.reason,
          suggestions: data.suggestions || []
        });
      } catch (error) {
        setUsernameStatus({ state: 'idle', message: '', suggestions: [] });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.username]);

  const calculateAge = (dob) => {
    if (!dob) return 0;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (usernameStatus.state === 'taken') {
      toast.error(usernameStatus.message);
      return;
    }

    if (scorePassword(formData.password).score < 2) {
      toast.error('Please choose a stronger password (mix letters, numbers and a symbol)');
      return;
    }

    if (!formData.agreeTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    if (!formData.confirmAge) {
      toast.error('You must be 18 or older to register');
      return;
    }

    if (!formData.date_of_birth) {
      toast.error('Please enter your date of birth');
      return;
    }

    const age = calculateAge(formData.date_of_birth);
    if (age < 18) {
      toast.error('You must be 18 or older to register');
      return;
    }

    setLoading(true);
    const result = await register({
      name: formData.name,
      username: formData.username.trim(),
      email: formData.email,
      password: formData.password,
      date_of_birth: formData.date_of_birth,
      captcha_id: formData.captcha_id,
      captcha_answer: formData.captcha_answer
    });

    setLoading(false);

    if (result.success) {
      toast.success('Registration successful! Please check your email for the verification code.');
      navigate('/verify-email');
    } else {
      toast.error(result.error);
      setFormData((prev) => ({ ...prev, captcha_answer: '' }));
      setCaptchaKey((k) => k + 1);
    }
  };

  const statusColor = usernameStatus.state === 'available'
    ? 'text-emerald-400'
    : usernameStatus.state === 'taken'
      ? 'text-rose-400'
      : 'text-muted-foreground';

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)] p-8">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-light mb-2 text-center" data-testid="register-heading">Create Account</h1>
          <p className="text-sm text-muted-foreground text-center mb-8">Join TruthConfession today</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                autoComplete="off"
                placeholder="johndoe"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                data-testid="register-username-input"
              />
              {usernameStatus.state === 'idle' ? (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="username-rules-hint">
                  3-20 characters. Letters, numbers, dots and underscores. Must start with a letter.
                </p>
              ) : (
                <p className={`text-[11px] mt-1 flex items-center gap-1 ${statusColor}`} data-testid="username-availability-status">
                  {usernameStatus.state === 'checking' && <CircleNotch size={12} className="animate-spin" />}
                  {usernameStatus.state === 'available' && <CheckCircle size={12} weight="fill" />}
                  {usernameStatus.state === 'taken' && <XCircle size={12} weight="fill" />}
                  {usernameStatus.message}
                </p>
              )}
              {usernameStatus.suggestions.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="username-suggestions">
                  <span className="text-[11px] text-muted-foreground">Try:</span>
                  {usernameStatus.suggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, username: suggestion }))}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-amber-400/40 text-amber-200 hover:bg-amber-400/10 transition-colors"
                      data-testid={`username-suggestion-${suggestion}`}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="register-password-input"
              />
              <PasswordStrengthMeter password={formData.password} />
            </div>

            <div>
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <Input
                id="date_of_birth"
                type="date"
                max={new Date().toISOString().split('T')[0]}
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                required
                data-testid="register-dob-input"
              />
            </div>

            <Captcha
              answer={formData.captcha_answer}
              reloadKey={captchaKey}
              onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
            />

            <div className="flex items-center space-x-2">
              <Checkbox
                id="confirmAge"
                checked={formData.confirmAge}
                onCheckedChange={(checked) => setFormData({ ...formData, confirmAge: checked })}
                data-testid="register-consent-checkbox"
              />
              <Label htmlFor="confirmAge" className="text-xs font-normal cursor-pointer">
                I confirm that I am 18 years or older
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="agreeTerms"
                checked={formData.agreeTerms}
                onCheckedChange={(checked) => setFormData({ ...formData, agreeTerms: checked })}
                data-testid="register-terms-checkbox"
              />
              <Label htmlFor="agreeTerms" className="text-xs font-normal cursor-pointer">
                I agree to the terms and conditions
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full rounded-full bg-primary hover:bg-primary/90"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
